import { TableCellLayout } from "@fluentui/react-components";
import { Translation } from "react-i18next";
import { DisplayField } from "../components/DisplayField";
import DocumentGridActivitiesCell from "../components/tool/DocumentGridActivitiesCell";
import DocumentGridFileNameCell from "../components/tool/DocumentGridFileNameCell";
import {
  ChaptersColumn,
  CreatedByFieldColumn,
  CreatedFieldColumn,
  DraftAdminDisplayNameColumn,
  DraftCreatedByFieldColumn,
  DraftToolTitleColumn,
  ModifiedByFieldColumn,
  ModifiedFieldColumn,
  PublishedAdminDisplayNameColumn,
  PublishedToolTitleColumn,
  ToolFileIconColumn,
  ToolIdFieldColumn,
  VersionFieldColumn,
  VersionIdFieldColumn,
  VsaTableColumnDefinition,
} from "../constants/DataGridColumns";
import {
  ContactPersonFieldUniqueName,
  ImprovementLeaderFieldUniqueName,
  ProcessFieldUniqueName,
  PublishAsGeneratedPdfFieldUniqueName,
  SupportingRoleFieldUniqueName,
} from "../constants/Fields";
import { getPublishAsGeneratedPdfDisplayValue } from "../constants/PublishAsGeneratedPdf";
import {
  DocumentGridFieldValues,
  DocumentGridViewItem,
} from "../models/DocumentGridViewItem";
import { Field } from "../models/Field";
import { FieldTypes, NumFieldType } from "../models/FieldTypes";
import { UrlFieldValue } from "../models/FieldValues";
import { UserInformation } from "../models/UserInformation";
import { FlatVsaaTag } from "../models/VsaaTag";
import { VsaComment } from "../models/VsaComment";
import { compareTextField, getTypedFieldType } from "./FieldService";
import { getFileNameWithoutExtension, sanitizeFileName } from "./ToolService";

interface DocumentGridColumnOptions {
  showNewDocumentIndicator?: boolean;
}

const getDocumentGridName = (item: DocumentGridViewItem): string =>
  sanitizeFileName(item.webUrl ?? "");

const getDocumentGridFileName = (item: DocumentGridViewItem): string =>
  item.fileName || getFileNameWithoutExtension(item.webUrl);

const createDocumentGridNameColumn = (
  showNewIndicator: boolean
): VsaTableColumnDefinition<DocumentGridViewItem> => ({
  columnId: "name",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("name")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <DocumentGridFileNameCell
      fileName={getDocumentGridName(item)}
      item={item}
      showNewIndicator={showNewIndicator}
    />
  ),
  compare: (firstItem, secondItem) =>
    compareTextField(
      getDocumentGridName(firstItem),
      getDocumentGridName(secondItem)
    ),
});

const createDocumentGridFileNameColumn = (
  showNewIndicator: boolean
): VsaTableColumnDefinition<DocumentGridViewItem> => ({
  columnId: "fileName",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("fileName")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <DocumentGridFileNameCell
      fileName={getDocumentGridFileName(item)}
      item={item}
      showNewIndicator={showNewIndicator}
    />
  ),
  compare: (firstItem, secondItem) =>
    compareTextField(
      getDocumentGridFileName(firstItem),
      getDocumentGridFileName(secondItem)
    ),
});

/**
 * Column definition that renders the related-activities cell for a document
 * grid row. Sorting is disabled because the cell shows a count and dialog,
 * not a sortable scalar.
 */
const DocumentGridActivitiesColumn: VsaTableColumnDefinition<DocumentGridViewItem> =
  {
    columnId: "activities",
    compare: "",
    renderHeaderCell: () => (
      <Translation>{(t) => <p>{t("activities")}</p>}</Translation>
    ),
    renderCell: (item) => <DocumentGridActivitiesCell item={item} />,
  };

/**
 * Column definition that renders the latest comment for a document grid row.
 * The backend exposes a single most-recent `Comment` (mapped to
 * `latestComment` on the client), so the cell renders its message directly.
 */
const DocumentGridLatestCommentColumn: VsaTableColumnDefinition<DocumentGridViewItem> =
  {
    columnId: "comments",
    compare: "",
    renderHeaderCell: () => (
      <Translation>{(t) => <p>{t("comments")}</p>}</Translation>
    ),
    renderCell: (item) => (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Comments}
              label={t("comments")}
              value={
                item.latestComment
                  ? ([item.latestComment] as VsaComment[])
                  : undefined
              }
            />
          )}
        </Translation>
      </TableCellLayout>
    ),
  };

/**
 * Maps the merged grid-view field values returned by the API into the value
 * shape expected by `DisplayField`. The grid view exposes every field as a
 * multi-value collection, so single-valued display types take the first
 * element when present.
 *
 * @param fields - The merged multi-value field set from the grid item.
 * @param field - The field definition describing how to render the value.
 * @returns The value to forward to `DisplayField`, or undefined when absent.
 */
const getDocumentFieldDisplayValue = (
  fields: DocumentGridFieldValues,
  field: Field
) => {
  const fieldType = getTypedFieldType(field.type);
  const name = field.uniqueName;

  if (
    fieldType === FieldTypes.Tag ||
    (fieldType === FieldTypes.Text && fields.tags?.[name])
  ) {
    return fields.tags?.[name];
  }

  if (
    fieldType === FieldTypes.BooleanCheckBox ||
    fieldType === FieldTypes.BooleanSwitch
  ) {
    const values = fields.booleans?.[name];
    return values && values.length > 0 ? values[0] : undefined;
  }

  if (fieldType === FieldTypes.Url) {
    const values = fields.urls?.[name];
    return values && values.length > 0 ? values[0] : undefined;
  }

  if (fieldType === FieldTypes.User || name === ContactPersonFieldUniqueName) {
    return fields.users?.[name];
  }

  // Default: prefer tags, then users, mirroring legacy behavior for unknown types.
  return fields.tags?.[name] ?? fields.users?.[name];
};

/**
 * Builds a column definition for a dynamic field on the document grid view.
 * Sorting is disabled for these columns because the underlying values are
 * multi-value collections that have no obvious single-key ordering.
 *
 * @param field - Field metadata.
 * @param hideBooleanField - When true, suppresses people/known link-only fields.
 * @returns A column definition, or undefined if the field should be skipped.
 */
const getDocumentColumnForField = (
  field: Field,
  hideBooleanField: boolean
): VsaTableColumnDefinition<DocumentGridViewItem> | undefined => {
  if (field.type === NumFieldType.PeoplePicker && hideBooleanField) {
    return;
  }

  if (
    hideBooleanField &&
    (field.uniqueName === ImprovementLeaderFieldUniqueName ||
      field.uniqueName === SupportingRoleFieldUniqueName ||
      field.uniqueName === PublishAsGeneratedPdfFieldUniqueName)
  ) {
    return;
  }

  if (field.uniqueName === PublishAsGeneratedPdfFieldUniqueName) {
    return {
      columnId: field.uniqueName,
      compare: "",
      renderHeaderCell: () => "Publiceringsformat",
      renderCell: (item) => {
        const value = item.fields.booleans?.[field.uniqueName]?.[0];

        return (
          <TableCellLayout>
            <DisplayField
              type={FieldTypes.Text}
              label="Publiceringsformat"
              value={getPublishAsGeneratedPdfDisplayValue(value)}
            />
          </TableCellLayout>
        );
      },
    };
  }

  const fieldType = getTypedFieldType(field.type);
  const isProcessField = field.uniqueName === ProcessFieldUniqueName;

  return {
    columnId: field.uniqueName,
    compare: "",
    renderHeaderCell: () => field.displayName,
    renderCell: (item) => {
      const value = getDocumentFieldDisplayValue(item.fields, field);
      return (
        <TableCellLayout>
          <DisplayField
            type={fieldType}
            label={field.displayName}
            value={value}
            showPath={isProcessField}
            linkToClientApp={isProcessField}
          />
        </TableCellLayout>
      );
    },
  };
};

/**
 * Returns the standard (non-dynamic) document grid column for a known field
 * name, or undefined if the name is not a recognized standard column.
 */
const getDocumentStandardFieldColumn = (
  fieldName: string
): VsaTableColumnDefinition<DocumentGridViewItem> | undefined => {
  switch (fieldName) {
    case "versionId":
      return VersionIdFieldColumn as VsaTableColumnDefinition<DocumentGridViewItem>;
    case "modified":
      return ModifiedFieldColumn;
    case "modifiedBy":
      return ModifiedByFieldColumn;
    case "created":
      return CreatedFieldColumn;
    case "createdBy":
      return CreatedByFieldColumn;
    case "draftCreatedBy":
      return DraftCreatedByFieldColumn;
    case "comments":
      return DocumentGridLatestCommentColumn;
  }
};

/**
 * Builds the column list for the published document grid based on the field
 * order returned by the document view definition. Falls back to activity
 * field metadata for fields not defined directly on documents.
 *
 * @param standardFields - Standard field names from the document view definition.
 * @param documentFields - Field metadata defined directly on documents.
 * @param activityFields - Field metadata defined on activities, used to
 *   resolve inherited grid columns.
 * @param fieldOrder - The ordered list of column names to render.
 * @returns Ordered column definitions for the document grid.
 */
export const getDocumentGridColumnsFromColumnOrder = (
  standardFields: string[],
  documentFields: Field[],
  activityFields: Field[],
  fieldOrder: string[],
  isDraft: boolean = false,
  options: DocumentGridColumnOptions = {}
): VsaTableColumnDefinition<DocumentGridViewItem>[] => {
  const columns: VsaTableColumnDefinition<DocumentGridViewItem>[] = [];
  const showNewIndicator = options.showNewDocumentIndicator === true;

  fieldOrder.forEach((fieldName) => {
    switch (fieldName) {
      case "toolId":
        columns.push(
          ToolIdFieldColumn as VsaTableColumnDefinition<DocumentGridViewItem>
        );
        if (!isDraft) {
          columns.push(
            VersionFieldColumn as VsaTableColumnDefinition<DocumentGridViewItem>
          );
        }
        return;
      case "title":
        columns.push(
          (isDraft
            ? DraftToolTitleColumn
            : PublishedToolTitleColumn) as VsaTableColumnDefinition<DocumentGridViewItem>
        );
        return;
      case "type":
        columns.push(
          ToolFileIconColumn as unknown as VsaTableColumnDefinition<DocumentGridViewItem>
        );
        return;
      case "name":
        columns.push(createDocumentGridNameColumn(showNewIndicator));
        return;
      case "fileName":
        columns.push(createDocumentGridFileNameColumn(showNewIndicator));
        return;
      case "adminDisplayName":
        columns.push(
          (isDraft
            ? DraftAdminDisplayNameColumn
            : PublishedAdminDisplayNameColumn) as unknown as VsaTableColumnDefinition<DocumentGridViewItem>
        );
        return;
      case "chapters":
        columns.push(
          ChaptersColumn as unknown as VsaTableColumnDefinition<DocumentGridViewItem>
        );
        return;
      case "activities":
        columns.push(DocumentGridActivitiesColumn);
        return;
    }

    const standard = getDocumentStandardFieldColumn(fieldName);
    if (standard) {
      columns.push(standard);
      return;
    }

    if (standardFields.includes(fieldName)) {
      // Standard field name without a dedicated renderer; skip silently.
      return;
    }

    const documentField = documentFields.find(
      (f) => f.uniqueName === fieldName
    );
    if (documentField) {
      const column = getDocumentColumnForField(documentField, false);
      if (column) {
        columns.push(column);
      }
      return;
    }

    const activityField = activityFields.find(
      (f) => f.uniqueName === fieldName
    );
    if (activityField) {
      const column = getDocumentColumnForField(activityField, false);
      if (column) {
        columns.push(column);
      }
    }
  });

  return columns;
};

/**
 * Ensures every grid item has an `id` (required by the generic data grid's
 * row id accessor) by mapping `versionId` into `id` when missing.
 *
 * @param items - The raw items returned by the API.
 * @returns The same items with `id` populated for each row.
 */
export const mapDocumentGridItemIds = (
  items: DocumentGridViewItem[]
): DocumentGridViewItem[] =>
  items.map((item) => ({
    ...item,
    id: item.id ?? item.versionId,
  }));

/**
 * Flattens the merged multi-value `fields` of a document grid item into the
 * Tool-shaped `FieldValues` structure used by the quick-edit grid and the
 * draft save mapping. Only the fields defined directly on documents (per the
 * supplied document field metadata) are projected, because activity-inherited
 * fields are not editable on a document and must not round-trip on save.
 *
 * Document-defined boolean and url fields are server-merged from the tool
 * alone, so flattening multi-value arrays into single values is lossless.
 *
 * @param items - The raw draft items returned by the API.
 * @param documentFields - Field metadata defined directly on documents.
 * @returns Items extended with `id` and a Tool-shaped `fieldValues`.
 */
export const mapDocumentGridDraftItems = (
  items: DocumentGridViewItem[],
  documentFields: Field[]
): DocumentGridViewItem[] =>
  items.map((item) => {
    const fieldValues = {
      booleans: {} as Record<string, boolean>,
      tags: {} as Record<string, FlatVsaaTag[] | null>,
      users: {} as Record<string, UserInformation[] | null>,
      urls: {} as Record<string, UrlFieldValue | null>,
      stringArrays: {} as Record<string, string[] | null>,
    };

    documentFields.forEach((field) => {
      const name = field.uniqueName;
      const fieldType = getTypedFieldType(field.type);

      if (fieldType === FieldTypes.Tag) {
        const tags = item.fields.tags?.[name];
        if (tags) {
          fieldValues.tags[name] = tags;
        }
      } else if (
        fieldType === FieldTypes.BooleanCheckBox ||
        fieldType === FieldTypes.BooleanSwitch
      ) {
        const values = item.fields.booleans?.[name];
        if (values && values.length > 0) {
          fieldValues.booleans[name] = values[0];
        }
      } else if (fieldType === FieldTypes.User) {
        const users = item.fields.users?.[name];
        if (users) {
          fieldValues.users[name] = users;
        }
      } else if (fieldType === FieldTypes.Url) {
        const values = item.fields.urls?.[name];
        if (values && values.length > 0) {
          fieldValues.urls[name] = values[0];
        }
      } else {
        const values = item.fields.stringArrays?.[name];
        if (values) {
          fieldValues.stringArrays[name] = values;
        }
      }
    });

    return {
      ...item,
      id: item.id ?? item.versionId,
      fieldValues,
    };
  });
